/**
 * Rewards API client.
 *
 * Every call goes through the proxy. There is deliberately no client-side RTDB read of
 * `rewards/*` here: the wallet's lots are money, the liability figure has to be derived
 * from them server-side, and a browser that can read the tree is a browser someone will
 * eventually be tempted to let write to it.
 */
import { authHeaders } from '../../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

const call = async (path, options = {}) => {
    const res = await fetch(`${PROXY_BASE}${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()), ...(options.headers || {}) },
    });
    const text = await res.text();
    let body = {};
    try { body = text ? JSON.parse(text) : {}; } catch {
        // An HTML body here means the request hit the SPA rewrite instead of the proxy —
        // the VITE_PROXY_URL build trap. Say so plainly rather than "unexpected token <".
        throw new Error(res.ok ? 'The rewards service is not reachable from this build.' : `Server returned ${res.status}`);
    }
    if (!res.ok) throw new Error(body.error || `Server returned ${res.status}`);
    return body;
};

const qs = (o) => {
    const p = new URLSearchParams();
    Object.entries(o || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
    const s = p.toString();
    return s ? `?${s}` : '';
};

export const rewardsService = {
    getConfig: () => call('/api/rewards/config'),
    saveConfig: (config) => call('/api/rewards/config', { method: 'POST', body: JSON.stringify(config) }),

    getOverview: () => call('/api/rewards/overview'),
    getWallets: (opts) => call(`/api/rewards/wallets${qs(opts)}`),
    getExpiring: (days = 30) => call(`/api/rewards/expiring${qs({ days })}`),

    getWallet: (mobile) => call(`/api/rewards/wallet${qs({ mobile })}`),
    getLedger: (mobile, limit = 100) => call(`/api/rewards/ledger${qs({ mobile, limit })}`),
    getOrderState: (orderKey) => call(`/api/rewards/order/${encodeURIComponent(orderKey)}`),

    // Manual promotional credits. Requesting and approving are separate calls on purpose:
    // the server refuses an approval from the person who made the request.
    lookupCustomer: (mobile) => call(`/api/rewards/customer-lookup${qs({ mobile })}`),
    listCredits: (opts) => call(`/api/rewards/credits${qs(opts)}`),
    requestCredit: (body) => call('/api/rewards/credits/request', { method: 'POST', body: JSON.stringify(body) }),
    approveCredit: (id) => call(`/api/rewards/credits/${encodeURIComponent(id)}/approve`, { method: 'POST' }),
    rejectCredit: (id, reason) => call(`/api/rewards/credits/${encodeURIComponent(id)}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

    // Reward campaigns: bulk credits. Creating one credits nobody — `runCampaign` does,
    // and it is idempotent per customer so a timed-out run is simply re-run.
    listCampaigns: () => call('/api/rewards/campaigns'),
    createCampaign: (body) => call('/api/rewards/campaigns', { method: 'POST', body: JSON.stringify(body) }),
    // A campaign built on the Quick Order screen lives with the other campaigns; one
    // built from a CSV here lives in the rewards tree. `kind` says which endpoint runs it,
    // so the screen does not have to care where it came from.
    runCampaign: (id, dry, kind = 'rewards') => (kind === 'offer'
        ? call(`/api/campaigns/${encodeURIComponent(id)}/credit-wallets${dry ? '?dry=1' : ''}`, { method: 'POST', body: '{}' })
        : call(`/api/rewards/campaigns/${encodeURIComponent(id)}/run${dry ? '?dry=1' : ''}`, { method: 'POST' })),
    preflightCampaign: (id) => call(`/api/campaigns/${encodeURIComponent(id)}/wallet-preflight`),
    deleteCampaign: (id, confirmCredited, kind = 'rewards') => (kind === 'offer'
        ? call('/api/campaigns/delete', { method: 'POST', body: JSON.stringify({ campaignId: id }) })
        : call(`/api/rewards/campaigns/${encodeURIComponent(id)}/delete`, { method: 'POST', body: JSON.stringify({ confirmCredited: !!confirmCredited }) })),

    /**
     * The recipient list, as a file. Fetched with the auth header rather than opened as a
     * link, because the endpoint needs a token and a plain <a href> cannot carry one.
     */
    downloadRecipients: async (id, only) => {
        const res = await fetch(`${PROXY_BASE}/api/rewards/campaigns/${encodeURIComponent(id)}/recipients.csv${only ? `?only=${only}` : ''}`, {
            headers: await authHeaders(),
        });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = url; a.download = `campaign-${id}-recipients.csv`; a.click();
        URL.revokeObjectURL(url);
    },

    /** A dry run reports what WOULD be credited and writes nothing. */
    sweep: ({ days = 30, dry = false } = {}) => call(`/api/rewards/sweep${qs({ days, dry: dry ? 1 : undefined })}`, { method: 'POST' }),
};

export default rewardsService;
